import trim from 'validator/lib/trim';
import equals from 'validator/lib/equals';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import moment from 'moment';

export type ValidationCheck = () => boolean;

export class StringValidationChecks {
    static isRequired(getProperty: () => string | null): ValidationCheck {
        return () => {
            const property = getProperty();

            if (property === null) {
                return false;
            }

            return !isEmpty(trim(property));
        };
    }

    static isRequiredIf(condition: () => boolean, getProperty: () => string | null): ValidationCheck {
        return () => {
            if (!condition()) {
                return true;
            }

            return this.isRequired(getProperty)();
        };
    }

    static isEmail(getProperty: () => string): ValidationCheck {
        return () => isEmail(getProperty());
    }

    static stringMatch(getProperty: () => string, getPropertyToCompare: () => string): ValidationCheck {
        return () =>  equals(getProperty(), getPropertyToCompare());
    }

    static stringNotContainedIn(getProperty: () => string, getPropertyToCompare: () => string[]): ValidationCheck {
        return () => getPropertyToCompare().indexOf(getProperty()) === -1;
    }

    static stringLengthIsLessThanCharLimit(getProperty: () => string, charLimit: number): ValidationCheck {
        return () => getProperty().length < charLimit;
    }
}

export class BooleanValidationChecks {
    static mustBeTrue(getProperty: () => boolean): ValidationCheck {
        return () => getProperty();
    }
}

export class NumberValidationChecks {
    static isValidNumber(getProperty: () => number): ValidationCheck {
        return () =>  !isNaN(getProperty());
    }
}

/*
** To be used on string fields representing dates and times with the following formats:
**    dates: YYYY-MM-DD
**    times: HH:mm
**    dateTimes: YYYY-MM-DD HH:mm
** All dates and times involved are local, not UTC
*/
export class DateValidationChecks {
    static isRequired(getProperty: () => string): ValidationCheck {
        return () => !isEmpty(trim(getProperty()));
    }

    static dateIsNotBefore(getRawDateLimit: () => string, getRawDate: () => string): ValidationCheck {
        return () => {
            const rawDateLimit = getRawDateLimit();
            const rawDate = getRawDate();

            if (isEmpty(trim(rawDate)) || isEmpty(trim(rawDateLimit))) {
                return true;
            }

            const date = moment(rawDate, 'YYYY-MM-DD');
            const dateLimit = moment(rawDateLimit, 'YYYY-MM-DD');

            return date.isSameOrAfter(dateLimit, 'day');
        };
    }

    static dateIsNotBeforeToday(getRawDate: () => string): ValidationCheck {
        return this.dateIsNotBefore(() => moment().format('YYYY-MM-DD'), getRawDate);
    }

    static dateIsNotAfter(getRawDateLimit: () => string, getRawDate: () => string): ValidationCheck {
        return () => {
            const rawDateLimit = getRawDateLimit();
            const rawDate = getRawDate();

            if (isEmpty(trim(rawDate)) || isEmpty(trim(rawDateLimit))) {
                return true;
            }

            const date = moment(rawDate, 'YYYY-MM-DD');
            const dateLimit = moment(rawDateLimit, 'YYYY-MM-DD');

            return date.isSameOrBefore(dateLimit, 'day');
        };
    }

    static timeIsNotBefore(
        getRawDateLimit: () => string,
        getRawTimeLimit: () => string,
        getRawDate: () => string,
        getRawTime: () => string
    ): ValidationCheck {
        return () => {
            const rawDateLimit = getRawDateLimit();
            const rawTimeLimit = getRawTimeLimit();

            const rawDate = getRawDate();
            const rawTime = getRawTime();

            if (isEmpty(trim(rawDate)) || isEmpty(trim(rawTime)) || isEmpty(trim(rawDateLimit)) || isEmpty(trim(rawTimeLimit))) {
                return true;
            }

            const dateTime = moment(rawDate + ' ' + rawTime, 'YYYY-MM-DD HH:mm');
            const dateTimeLimit = moment(rawDateLimit + ' ' + rawTimeLimit, 'YYYY-MM-DD HH:mm');

            if (!dateTime.isSame(dateTimeLimit, 'day')) {
                // If the date is not today then any issues with the flight time should be corrected on the date first
                return true;
            }

            return dateTime.isSameOrAfter(dateTimeLimit);
        };
    }

    static timeIsNotInPast(getRawDate: () => string, getRawTime: () => string): ValidationCheck {
        const now = moment();
        return this.timeIsNotBefore(() => now.format('YYYY-MM-DD'), () => now.format('HH:mm'), getRawDate, getRawTime);
    }
}
